import React from "react";
import _ from "lodash";

import {PortfolioManagerResource} from "../../../utils/api";
import {useTabsSharedDataContext} from "../components_v2/TabsSharedData/TabsSharedData";
import {generateQueryParamsHash, getPortfoliosDepotNumbersForHash} from "../utils";
import {isCancel, useCancelableRequest} from "../../../hooks/useCancelableRequest";

const SHARED_DATA_KEY = 'other-assets-data';

export function useOtherAssetsData(customerId, portfolios, startDate, endDate, withAssets=true, withLiabilities=false, withClearingAccounts=true, withDepots=false, excludedPortfolios, withExpiredAndFuture, cancelOnUnmount) {

  const [data, setData] = React.useState({
    data: null,
    loading: true,
    error: null,
    updatedAt: +new Date(),
  });
  const getCancelToken = useCancelableRequest(cancelOnUnmount);

  const paramsForHash = [startDate, endDate, withAssets, withLiabilities, withClearingAccounts, withDepots];
  const sharedData = useTabsSharedDataContext();

  const depotNumbersKey = getPortfoliosDepotNumbersForHash(portfolios);
  const hashKey = React.useMemo(
    () => generateQueryParamsHash(customerId, portfolios, ...paramsForHash),
    [customerId, depotNumbersKey, ...paramsForHash]
  );

  const updateSharedData = (state) => {
    sharedData.setDataValue(SHARED_DATA_KEY, hashKey, state);
  };

  const fetch = async () => {
    const cancelToken = getCancelToken();

    const _setState = (state, onlyCache=false) => {
      // It is not possible to sync data with shared data in
      // hook because hook is not executed if component destroyed
      if(!onlyCache) setData(state);
      updateSharedData(state, hashKey)
    };

    _setState({
      data: null,
      loading: true,
      error: null,
      updatedAt: +new Date()
    });

    try {
      const response = await PortfolioManagerResource.at(`customer/${customerId}/other-assets/`).get({
        clearing_account: withClearingAccounts,
        with_other_assets: withAssets,
        with_depots: withDepots,
        start_date: startDate,
        end_date: endDate,
        excluded_depot_id: !!excludedPortfolios ? excludedPortfolios : undefined, // undefined won't be send
        depot_id: portfolios.map(portfolio => portfolio.depotNumber),
        with_liabilities: withLiabilities,
        with_expired_and_future: withExpiredAndFuture,
      }, cancelToken);

      _setState({
        data: response,
        loading: false,
        error: null,
        updatedAt: +new Date()
      })

    } catch (error) {
      _setState({
        data: null,
        loading: false,
        error: error,
        updatedAt: +new Date()
      }, isCancel(error))
    }
  };

  const syncFromShared = () => {
    const sharedPortfolioData = !!sharedData
      ? sharedData.getDataValue(SHARED_DATA_KEY, hashKey)
      : undefined;

    if (!!sharedPortfolioData && !sharedPortfolioData.error && sharedPortfolioData.updatedAt !== data.updatedAt) {
      // sync internal hook data with shared data.
      setData(sharedPortfolioData);
      return true;
    } else {
      return false;
    }
  };

  React.useEffect(() => {syncFromShared()}, [_.get(sharedData, 'updatedAt')]);

  React.useEffect(() => {
    if (!!customerId) {

      // Already have usable cached data -> adopt it.
      const synced = syncFromShared();
      if (synced) return;

      fetch();
    }
  }, [customerId, JSON.stringify(portfolios), startDate, endDate]);

  return [data, fetch, updateSharedData]

}